import React from 'react';
import { FileCheck, ArrowRight, AlertCircle, Shield, DollarSign } from 'lucide-react';
import { motion } from 'framer-motion';

const Services: React.FC = () => {
  // Variantes de animação
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const item = {
    hidden: { y: 20, opacity: 0 },
    show: { y: 0, opacity: 1, transition: { type: 'spring', damping: 12 } }
  };

  const checkItem = {
    hidden: { scale: 0, opacity: 0 },
    show: { scale: 1, opacity: 1, transition: { type: 'spring', damping: 10 } }
  };

  return (
    <section className="py-16 md:py-24 bg-gradient-to-br from-white to-gray-100">
      <div className="container mx-auto px-4 md:px-6">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={container}
          className="max-w-6xl mx-auto"
        >
          {/* Cabeçalho da seção */}
          <motion.div className="text-center mb-12" variants={item}>
            <motion.div 
              className="inline-block bg-accent/10 text-accent px-4 py-1.5 rounded-full mb-4 font-medium text-sm"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              Consultoria Jurídica Especializada
            </motion.div>
            <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl mb-6 text-primary">
              Serviços
            </h2>
            <div className="max-w-3xl mx-auto">
              <h3 className="text-xl md:text-2xl font-semibold mb-3 text-primary/80">
                Análise e Revisão de Contratos Bancários
              </h3>
              <p className="text-gray-600 text-lg">
                Por Meio Judicial e Administrativo
              </p>
            </div>
          </motion.div>

          {/* Descrição principal */}
          <motion.div 
            variants={item}
            className="bg-white rounded-2xl shadow-xl p-6 md:p-8 lg:p-10 mb-12 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-accent/5 rounded-bl-full"></div>
            
            <p className="text-lg mb-8 text-gray-700 max-w-4xl">
              Nosso escritório realiza uma avaliação criteriosa de contratos bancários, com o objetivo de localizar vícios e abusos cometidos por bancos e financeiras, incluindo:
            </p>

            <div className="grid gap-6 md:grid-cols-2">
              {/* Lista de serviços com ícones checkmark */}
              <motion.div 
                className="bg-gray-50 rounded-xl p-5 border border-gray-100 hover:shadow-md transition-all"
                variants={checkItem}
                whileHover={{ y: -5 }}
              >
                <div className="flex items-start">
                  <div className="bg-accent/10 p-2 rounded-full mr-4">
                    <DollarSign className="text-accent" size={20} />
                  </div>
                  <p className="text-gray-700">
                    <span className="text-accent font-medium">✔</span> Juros acima dos limites legais e capitalização disfarçada.
                  </p>
                </div>
              </motion.div>

              <motion.div 
                className="bg-gray-50 rounded-xl p-5 border border-gray-100 hover:shadow-md transition-all"
                variants={checkItem}
                whileHover={{ y: -5 }}
              >
                <div className="flex items-start">
                  <div className="bg-accent/10 p-2 rounded-full mr-4">
                    <AlertCircle className="text-accent" size={20} />
                  </div>
                  <p className="text-gray-700">
                    <span className="text-accent font-medium">✔</span> Cobranças indevidas vinculadas à contratação de produtos e serviços.
                  </p>
                </div>
              </motion.div>

              <motion.div 
                className="bg-gray-50 rounded-xl p-5 border border-gray-100 hover:shadow-md transition-all"
                variants={checkItem}
                whileHover={{ y: -5 }}
              >
                <div className="flex items-start">
                  <div className="bg-accent/10 p-2 rounded-full mr-4">
                    <Shield className="text-accent" size={20} />
                  </div>
                  <p className="text-gray-700">
                    <span className="text-accent font-medium">✔</span> Penhoras sobre bens resguardados por lei e bloqueios desproporcionais.
                  </p>
                </div>
              </motion.div>

              <motion.div 
                className="bg-gray-50 rounded-xl p-5 border border-gray-100 hover:shadow-md transition-all"
                variants={checkItem}
                whileHover={{ y: -5 }}
              >
                <div className="flex items-start">
                  <div className="bg-accent/10 p-2 rounded-full mr-4">
                    <FileCheck className="text-accent" size={20} />
                  </div>
                  <p className="text-gray-700">
                    <span className="text-accent font-medium">✔</span> Erros processuais que permitam reverter ou suspender ações em andamento.
                  </p>
                </div>
              </motion.div>
            </div>

            <motion.p 
              className="mt-8 text-lg text-gray-700"
              variants={item}
            >
              Com base nessa análise, promovemos ações para contestar tais práticas, renegociar dívidas e defender os interesses de nossos clientes com assertividade e responsabilidade.
            </motion.p>
            
            <motion.div 
              className="mt-10 flex justify-center md:justify-start"
              variants={item}
              whileHover={{ scale: 1.05 }}
            >
              <a 
                href="#contact-form" 
                className="bg-gradient-to-r from-accent to-accent/80 hover:from-accent/90 hover:to-accent/70 text-white font-semibold py-3 px-8 rounded-full inline-flex items-center group"
              >
                Fale com um especialista
                <motion.div
                  animate={{ x: [0, 5, 0] }}
                  transition={{ repeat: Infinity, duration: 1.5, repeatDelay: 1 }}
                >
                  <ArrowRight size={18} className="ml-2" />
                </motion.div>
              </a>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
